const { debounce } = window.BAO.Utils

class CollectionHeaderScroll extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.scrollToActiveItem()
    this.updateArrows()
  }

  static get requiredElements () {
    return ['container', 'list', 'item', 'previous', 'next']
  }

  setupListeners () {
    if (this.nextEl) {
      this.listeners.add(this.nextEl, 'click', this.handleNextClick.bind(this))
    }

    if (this.previousEl) {
      this.listeners.add(
        this.previousEl,
        'click',
        this.handlePreviousClick.bind(this),
      )
    }

    this.listeners.add(
      this.listEl,
      'scroll',
      debounce(this.handleUpdate.bind(this), 100),
    )

    this.listeners.add(
      window,
      'resize',
      debounce(this.handleUpdate.bind(this), 100),
    )

    this.listeners.add(window, 'load', this.updateArrows.bind(this))

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.GRID_SWITCHER.SWITCHED_LAYOUT,
      this.handleUpdate.bind(this),
    )
  }

  updateArrows () {
    // Add the left arrow if the menu is overflowing to the right
    if (this.nextRightPosition > 0) {
      this.containerEl.setAttribute('data-fade-right', 'true')
      this.nextEl?.setAttribute('aria-hidden', 'false')
    } else {
      this.containerEl.setAttribute('data-fade-right', 'false')
      this.nextEl?.setAttribute('aria-hidden', 'true')
    }

    // Add the right arrow if the menu is overflowing to the left
    if (this.nextLeftPosition < 0) {
      this.containerEl.setAttribute('data-fade-left', 'true')
      this.previousEl?.setAttribute('aria-hidden', 'false')
    } else {
      this.containerEl.setAttribute('data-fade-left', 'false')
      this.previousEl?.setAttribute('aria-hidden', 'true')
    }
  }

  scrollToActiveItem () {
    if (!window.BAO.MediaBreakpoints.isMd) {
      return
    }

    const activeItem = this.els?.current?.element

    if (!activeItem) return

    // Wait a tick in case layout isn't ready yet
    requestAnimationFrame(() => {
      const containerRect = this.listEl.getBoundingClientRect()
      const itemRect = activeItem.getBoundingClientRect()

      const offset =
        itemRect.left -
        containerRect.left -
        (containerRect.width / 2 - itemRect.width / 2)

      this.listEl.scrollBy({
        left: offset,
        top: 0,
        behavior: 'auto',
      })
    })
  }

  handleUpdate () {
    setTimeout(this.updateArrows.bind(this), 100)
  }

  handlePreviousClick (e) {
    e.preventDefault()

    this.listEl.scrollBy({
      left: this.nextLeftPosition,
      top: 0,
      behavior: 'smooth',
    })
  }

  handleNextClick (e) {
    e.preventDefault()

    this.listEl.scrollBy({
      left: this.nextRightPosition,
      top: 0,
      behavior: 'smooth',
    })
  }

  get nextRightPosition () {
    if (!this.els.item?.elements.length) return 0

    const numElems = this.els.item.elements.length

    let nextRightElem = this.els.item.elements[numElems - 1]

    // Get the link element with the smallest right boundary.
    this.els.item.elements.some(item => {
      const rect = item.getBoundingClientRect()

      if (rect.left > this.listPositionRight) {
        nextRightElem = item

        return true
      }

      return false
    })

    const newPosition =
      nextRightElem.getBoundingClientRect().right - this.listPositionRight

    return newPosition === 0 ? 0 : Math.floor(newPosition)
  }

  get nextLeftPosition () {
    if (!this.els.item?.elements.length) return 0

    let nextLeftElem = this.els.item.elements[0]

    // Get the link element with the smallest left boundary.
    this.els.item.elements.some(item => {
      const rect = item.getBoundingClientRect()

      const difference = rect.left - this.listPositionLeft

      if (rect.left < this.listPositionLeft && difference < -5) {
        nextLeftElem = item

        return true
      }

      return false
    })

    const offset = nextLeftElem.getAttribute('data-first') !== 'true' ? 50 : 0

    const newPosition =
      nextLeftElem.getBoundingClientRect().left - this.listPositionLeft - offset

    return newPosition === 0 ? 0 : Math.ceil(newPosition)
  }

  get previousEl () {
    return this.els.previous?.element
  }

  get nextEl () {
    return this.els.next?.element
  }

  get containerEl () {
    return this.els.container?.element
  }

  get listEl () {
    return this.els.list.element
  }

  get listWidth () {
    return this.listEl.getBoundingClientRect().width
  }

  get listPositionRight () {
    return this.listEl.getBoundingClientRect().right
  }

  get listPositionLeft () {
    return this.listEl.getBoundingClientRect().left
  }
}

if (!customElements.get('collection-header-scroll')) {
  customElements.define('collection-header-scroll', CollectionHeaderScroll)
}
